import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import { useRegisterAPIMethod } from '@elfsight/widget-js-api';
import {
  FloatingButton,
  MatchDevices,
  Triggered,
  WidgetFontsLoader
} from '@elfsight/widget-ui';
import {
  documentScrollOver,
  elementInViewport,
  exitIntent,
  immediately,
  timeOnPageOver,
  truncateText,
  useStorage
} from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { API_SHOW_METHOD_NAME, meta } from '../constants';
import type { PopupContextType } from '../utils';
import { checkIsBar, PopupContext } from '../utils';
import { Layout } from './components/layout';
import type { LayoutVariation } from './components/layout/variations/types';
import { ReopenButtonIcon as Icon } from './components/ReopenButtonIcon';
import { MatchPages } from './match-pages';
import { MatchSchedule } from './match-schedule';

const prefixedAlias = `eapps-${meta.alias}`;

export function Widget() {
  const {
    editMode,
    displayDeactivation,
    owner,
    platform,
    widgetId,
    googleAnalytics,
    blocks = [],
    layout,
    widgetWidth,
    elementsAlignment,
    elementsAlignmentPane,
    backdropVisible,
    backdropClose,
    backdropBackground,
    closeButtonVisible,
    closeButtonColor,
    triggerPageLoadEnabled,
    triggerTimeOnPageEnabled,
    triggerTimeOnPageDuration,
    triggerScrollEnabled,
    triggerScrollPosition,
    triggerScrollToElementEnabled,
    triggerScrollToElementId,
    triggerExitIntentEnabled,
    triggerClickEnabled,
    triggerClickElementId,
    displayFrequency,
    displayPages,
    displaySpecificPages,
    displayExcludedPages,
    displayDevices,
    reopenButton,
    reopenButtonIcon,
    reopenButtonText,
    reopenButtonPosition,
    reopenButtonHorizontalPadding: derivedReopenButtonHorizontalPadding,
    reopenButtonVerticalPadding,
    reopenButtonMode,
    reopenButtonBorderRadius,
    reopenButtonColor,
    reopenButtonFontStyle,
    reopenButtonFontColor,
    reopenButtonFont
  } = useSettings();

  const [closed, { set: setClosed }] = useStorage<boolean>('closed', false);
  const [lastShow, { set: setLastShow }] = useStorage<number>('lastShow', 0);

  const [shouldOpenByTrigger, setShouldOpenByTrigger] = useState(true);
  const [openedByTrigger, setOpenedByTrigger] = useState(false);
  const [openedByUser, setOpenedByUser] = useState(editMode ? true : undefined);

  const openByUser = () => setOpenedByUser(true);
  const closeByUser = () => setOpenedByUser(false);

  const isBarLayout = checkIsBar(layout);

  const opened = openedByUser ?? openedByTrigger;

  const pages =
    {
      allPages: [],
      specificPages: displaySpecificPages,
      excludedPages: displayExcludedPages
    }[displayPages] || [];

  const encodedTrimmedPagesUrls = pages.reduce((acc, { url }) => {
    const trimmedUrl = url.trim();

    if (trimmedUrl.length) {
      acc.push(encodeURI(trimmedUrl));
    }

    return acc;
  }, [] as string[]);

  const triggers = useMemo(
    () =>
      [
        triggerPageLoadEnabled && immediately(),
        triggerTimeOnPageEnabled &&
          triggerTimeOnPageDuration &&
          timeOnPageOver('timeOnPageOver', triggerTimeOnPageDuration),
        triggerScrollEnabled &&
          triggerScrollPosition &&
          documentScrollOver('documentScrollOver', triggerScrollPosition / 100),
        triggerScrollToElementEnabled &&
          triggerScrollToElementId &&
          elementInViewport(
            'elementInViewport',
            `#${CSS.escape(triggerScrollToElementId)}`
          ),
        triggerExitIntentEnabled && exitIntent()
      ].filter((trigger) => typeof trigger === 'function'),
    [
      triggerPageLoadEnabled,
      triggerTimeOnPageEnabled,
      triggerTimeOnPageDuration,
      triggerScrollEnabled,
      triggerScrollPosition,
      triggerScrollToElementEnabled,
      triggerScrollToElementId,
      triggerExitIntentEnabled
    ]
  );

  const controlContext: PopupContextType = {
    opened: Boolean(openedByUser),
    close: () => {
      closeByUser();
      setClosed(true);
    },
    redirect: (url, target = '_blank') => {
      window.open(url, editMode ? '_blank' : target);
    }
  };

  useRegisterAPIMethod(API_SHOW_METHOD_NAME, openByUser);

  const handleTriggersActivation = useCallback(() => {
    if (!editMode && shouldOpenByTrigger) {
      setOpenedByTrigger(true);
      setLastShow(Math.trunc(Date.now() / 1000));
    }
  }, [editMode, shouldOpenByTrigger]);

  // the logic which prevents displaying of a popup more often than displayFrequency property says
  useLayoutEffect(() => {
    if (editMode) {
      return;
    }

    const now = Math.trunc(Date.now() / 1000);
    const parsedLastShow = parseInt(String(lastShow ?? 0), 10);
    const isClosed = !!closed;

    const isDayPassedSinceLastShow = now - parsedLastShow >= 86400;

    const resolvers = [
      () =>
        displayFrequency === 'weekly' &&
        parsedLastShow &&
        now - parsedLastShow < 604800,
      () =>
        displayFrequency === 'daily' &&
        parsedLastShow &&
        !isDayPassedSinceLastShow,
      () => displayFrequency === 'once' && parsedLastShow,
      () =>
        displayFrequency === 'everyTimeUntilClose' &&
        isClosed &&
        parsedLastShow &&
        !isDayPassedSinceLastShow
    ];

    for (const resolve of resolvers) {
      if (resolve()) {
        setShouldOpenByTrigger(false);
        return;
      }
    }
  }, [displayFrequency, editMode, lastShow]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    sendGoogleAnalyticsEvents({
      eventType: 'popupOpened',
      googleAnalyticsEvents: googleAnalytics,
      valueMapping: {
        widgetId
      }
    });
  }, [opened, googleAnalytics, widgetId]);

  // the implementation of triggerClickEnabled
  useEffect(() => {
    if (editMode || !triggerClickEnabled || !triggerClickElementId) {
      return;
    }

    const listener: EventListener = (event) => {
      const { target } = event;
      const isTargetElement =
        target instanceof HTMLElement &&
        !!target.closest(`#${CSS.escape(triggerClickElementId)}`);

      if (!isTargetElement) {
        return;
      }

      event.preventDefault();

      if (shouldOpenByTrigger) {
        openByUser();
        setLastShow(Math.trunc(Date.now() / 1000));
        handleTriggersActivation();
      }
    };

    document.addEventListener('click', listener, true);

    return () => {
      document.removeEventListener('click', listener, true);
    };
  }, [
    editMode,
    triggerClickEnabled,
    triggerClickElementId,
    handleTriggersActivation,
    shouldOpenByTrigger,
    setLastShow
  ]);

  // the logic which re-opens a popup in the editor after it's closed
  useEffect(() => {
    if (!editMode || openedByUser !== false || (reopenButton && !isBarLayout)) {
      return undefined;
    }

    const openingTimer = setTimeout(openByUser, 1000);

    return () => {
      if (openingTimer) {
        clearTimeout(openingTimer);
      }
    };
  }, [editMode, openedByUser, reopenButton, isBarLayout]);

  const isIconOnly = useMemo(() => {
    return !reopenButtonText?.trim();
  }, [reopenButtonText]);

  const reopenButtonHorizontalPadding = isIconOnly
    ? reopenButtonVerticalPadding
    : derivedReopenButtonHorizontalPadding;

  const ariaLabel = useMemo(() => {
    const visibleBlocks = blocks.filter(({ visible }) => visible);
    const rawAriaLabel =
      visibleBlocks.find(({ type }) => type === 'heading')?.text ||
      visibleBlocks.find(({ type }) => type === 'text')?.text ||
      '';

    return truncateText(rawAriaLabel);
  }, [blocks]);

  const main = (
    <PopupContext.Provider value={controlContext}>
      <>
        {!isBarLayout && reopenButton && !opened && (
          <>
            <WidgetFontsLoader fonts={[reopenButtonFont]} />

            <FloatingButton
              mode={reopenButtonMode}
              position={reopenButtonPosition}
              horizontalPadding={reopenButtonHorizontalPadding}
              verticalPadding={reopenButtonVerticalPadding}
              borderRadius={reopenButtonBorderRadius}
              color={reopenButtonColor}
              fontColor={reopenButtonFontColor}
              font={reopenButtonFontStyle}
              fontFamily={reopenButtonFont}
              iconOnly={isIconOnly}
              iconLeft={
                !reopenButtonIcon?.url
                  ? undefined
                  : () => (
                      <Icon
                        reopenButtonIcon={reopenButtonIcon}
                        size={reopenButtonFontStyle?.fontSize}
                      />
                    )
              }
              onClick={openByUser}
            >
              {reopenButtonText}
            </FloatingButton>
          </>
        )}

        <Layout
          ariaLabel={ariaLabel}
          appId={`${prefixedAlias}-${widgetId}`}
          show={opened}
          elements={blocks}
          variation={layout as LayoutVariation}
          width={parseInt(String(widgetWidth), 10)}
          elementsAlignment={elementsAlignment}
          elementsAlignmentPane={elementsAlignmentPane}
          controls={{
            closeButtonVisible,
            closeButtonColor
          }}
          overlayProps={{
            visible: backdropVisible,
            closeOnClick: backdropClose,
            background: backdropBackground
          }}
        />
      </>
    </PopupContext.Provider>
  );

  if (!blocks.length) {
    return null;
  }

  if (editMode) {
    return main;
  }

  return (
    (!displayDeactivation || (displayDeactivation && owner && platform)) && (
      <MatchSchedule>
        <MatchDevices list={displayDevices}>
          <MatchPages
            mode={displayPages === 'specificPages' ? 'include' : 'exclude'}
            list={encodedTrimmedPagesUrls}
            strict
          >
            <Triggered use={triggers} onActivate={handleTriggersActivation}>
              {main}
            </Triggered>
          </MatchPages>
        </MatchDevices>
      </MatchSchedule>
    )
  );
}
